import { motion } from 'framer-motion';
import { FaEye, FaDownload, FaTrash, FaCalendar, FaFileAlt } from 'react-icons/fa';
import PDFThumbnail from './PDFThumbnail';
import UploadButton from './UploadButton';
import { formatFileSize } from '../../utils/helpers';

const SampleCard = ({ sample, onView, onUpload, onDelete }) => {
  const editionColor = sample.edition === 'micro' 
    ? 'from-accent-blue/20 to-primary-green-light/20'
    : 'from-primary-green-light/20 to-accent-yellow/20';

  const editionBadge = sample.edition === 'micro'
    ? { bg: 'bg-accent-blue', icon: '🌈', label: 'Micro' }
    : { bg: 'bg-accent-orange', icon: '🚀', label: 'Macro' };

  const hasContent = sample.pdfUrl || sample.thumbnail;

  return (
    <motion.div
      whileHover={{ y: -5 }}
      className={`card relative overflow-hidden bg-gradient-to-br ${editionColor} border-2 border-transparent hover:border-primary-green transition-all h-full flex flex-col`}
    >
      {/* Badge */}
      <div className={`absolute top-4 right-4 ${editionBadge.bg} text-white px-3 py-1 rounded-full font-display font-bold text-sm shadow-lg z-10 flex items-center gap-1`}>
        <span>{editionBadge.icon}</span>
        <span>{editionBadge.label}</span>
      </div>

      {/* Thumbnail */}
      <div className="relative mb-4">
        <PDFThumbnail 
          sample={sample}
          onClick={hasContent ? onView : null}
        />
        
        {hasContent && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onView}
            className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center"
          >
            <div className="text-white">
              <FaEye className="text-5xl mb-2 mx-auto" />
              <span className="font-display font-bold text-lg">View Full PDF</span>
            </div>
          </motion.button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col">
        <h3 className="text-2xl font-display font-bold text-primary-green-dark mb-2">
          {sample.title}
        </h3>

        {/* Metadata */}
        <div className="space-y-2 mb-4">
          {sample.uploadDate && (
            <div className="flex items-center gap-2 text-sm text-neutral-gray-dark">
              <FaCalendar className="text-primary-green" />
              <span>{new Date(sample.uploadDate).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {/* Description */}
        {sample.description && (
          <p className="text-neutral-gray-dark mb-4 leading-relaxed">
            {sample.description}
          </p>
        )}

        {/* Actions */}
        <div className="mt-auto space-y-2">
          {hasContent ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                <motion.button
                  onClick={onView}
                  className="flex items-center justify-center gap-2 bg-primary-green text-white px-4 py-3 rounded-lg font-display font-semibold shadow-card hover:shadow-card-hover transition-all"
                >
                  <FaEye /> <span>View</span>
                </motion.button>

                <motion.a
                  href={sample.pdfUrl}
                  download={sample.fileName || sample.title}
                  className="flex items-center justify-center gap-2 bg-accent-orange text-white px-4 py-3 rounded-lg font-display font-semibold shadow-card hover:shadow-card-hover transition-all"
                >
                  <FaDownload /> <span>Download</span>
                </motion.a>
              </div>

              {/* ✅ ONLY SHOW DELETE BUTTON IF onDelete IS PASSED (Admin Mode) */}
              {onDelete && (
                <motion.button
                  onClick={() => {
                    if (window.confirm('Are you sure?')) onDelete(sample.id);
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-red-500 text-white px-4 py-2 rounded-lg font-display font-semibold text-sm hover:bg-red-600 transition-colors"
                >
                  <FaTrash /> <span>Delete Sample</span>
                </motion.button>
              )}
            </>
          ) : (
            /* ✅ IF NO CONTENT: Show Upload button (Admin) OR "Coming Soon" (Public) */
            onUpload ? (
              <UploadButton sampleId={sample.id} onUpload={onUpload} />
            ) : (
              <div className="text-center py-8 bg-white/50 rounded-xl border-2 border-dashed border-primary-green/20">
                <div className="text-4xl mb-2">🚧</div>
                <h4 className="font-display font-bold text-primary-green-dark">Coming Soon!</h4>
                <p className="text-sm text-neutral-gray-dark">This issue is being prepared.</p>
              </div>
            )
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default SampleCard;